import React, { useState } from "react";

// Assuming styles is defined elsewhere, but for a single file,
// we'll move styles inline using Tailwind classes.

// ---------------------------------------------
// NORMALISE PROPERTY ADDRESS FOR DISPLAY (String)
// ---------------------------------------------
function normaliseAddress(addr) {
  if (!addr) return "N/A";

  // Already a plain string
  if (typeof addr === "string") return addr;

  // Google Places object → formatted string
  return (
    addr.formatted ||
    [
      addr.unit ? `${addr.unit}/` : "",
      addr.street || "",
      addr.suburb || "",
      addr.state || "",
      addr.postcode || "",
    ]
      .filter(Boolean)
      .join(" ")
      .trim()
  );
}

// ---------------------------------------------
// GET STRUCTURED PROPERTY ADDRESS FOR LAMBDA (Object)
// ---------------------------------------------
function getStructuredAddress(addr) {
  if (typeof addr === "string" || !addr) {
    // If only a string or empty, provide minimal structure
    return { street: addr || "N/A", suburb: "N/A", state: "N/A", postcode: "N/A" };
  }
  // Otherwise, assume it's the structured Google Places object the Lambda expects
  return {
    street: addr.street || addr.formatted,
    suburb: addr.suburb,
    state: addr.state,
    postcode: addr.postcode,
    // Include the original formatted string for the Lambda's note summary
    formattedAddress: addr.formatted || normaliseAddress(addr), 
  };
}

// ---------------------------------------------
// CREATE FINAL PAYLOAD MAPPING TO LAMBDA'S EXPECTATIONS
// ---------------------------------------------
function createFinalPayload(formData) {
  const structuredAddress = getStructuredAddress(formData.propertyAddress);
  
  // Extract and flatten liabilities
  const applicantLiabilities = formData.applicant1?.liabilities || [];
  const coApplicantLiabilities = formData.applicant2?.liabilities || [];

  return {
    // Spread all original form data first
    ...formData,

    // 1. Correct the Address mapping
    propertyAddress: structuredAddress,

    // 2. Map flat keys the Lambda expects
    propertyValue: formData.purchasePrice || formData.estimatedPropertyValue || 0,
    loanBalance: formData.currentLoanBalance || 0,
    interestRate: formData.currentInterestRate || 0,
    currentLender: formData.currentLender || "",

    // 3. Map flat Liability arrays the Lambda expects
    applicantLiabilities: applicantLiabilities,
    coApplicantLiabilities: coApplicantLiabilities,

    // 4. Correct Loan Purpose
    loanPurpose: formData.applicationType === "purchase" ? "Purchase" : "Refinance",

    // 5. Correct Employment key names within nested applicant objects
    applicant1: {
      ...formData.applicant1,
      // Map employerName (UI key) to employer (Lambda key)
      employer: formData.applicant1?.employerName,
      // Remove the original employerName to avoid redundancy if needed, but safe to keep
    },
    applicant2: {
      ...formData.applicant2,
      employer: formData.applicant2?.employerName,
    },

    // 6. Include the refinance goal
    refinanceGoal: formData.refinanceGoal || "N/A",
  };
}


// Mock component for file isolation
function WelcomeScreen({ formData }) {
  const primaryApplicantName = `${formData.applicant1?.firstName || "Valued"} ${formData.applicant1?.lastName || "Client"}`;
  
  return (
    <div className="p-8 text-center bg-white rounded-xl shadow-lg border border-green-200">
      <svg className="mx-auto h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <h3 className="mt-4 text-2xl font-semibold text-gray-900">Application Submitted!</h3>
      <p className="mt-2 text-gray-500">
        Thank you, <span className="font-medium text-green-600">{primaryApplicantName}</span>. Your details have been securely sent to Mortgage Matrix.
      </p>
      <p className="mt-4 text-sm text-gray-700">
        A broker will be in touch shortly to discuss your refinancing goals.
      </p>
    </div>
  );
}


function SummarySubmit({ formData = {} }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async () => {
    setErrorMsg('');
    setSubmitting(true);

    try {
      const payload = createFinalPayload(formData);
      
      console.log("📬 Final payload sent to Lambda:", payload);

      // Implement exponential backoff for API calls
      const maxRetries = 3;
      let attempt = 0;
      let response;

      while (attempt < maxRetries) {
        try {
          response = await fetch(
            "https://duh7zoxbvd.execute-api.ap-southeast-2.amazonaws.com/default/MortgageMatrixSendToMercury",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            }
          );

          if (response.ok) {
            break; // Success, exit loop
          }

          if (attempt === maxRetries - 1) {
            throw new Error(`Server responded with ${response.status}`);
          }
        } catch (err) {
          if (attempt === maxRetries - 1) {
            throw err; // Final attempt failed
          }
          // Exponential backoff delay (1s, 2s, 4s)
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt)));
        }
        attempt++;
      }
      
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }
      
      const result = await response.json();
      const data =
        typeof result.body === "string" ? JSON.parse(result.body) : result;

      console.log("📩 Lambda Response Parsed:", data);

      if (data.success) {
        console.log("✅ Mercury submission successful:", data);
        setSubmitted(true);
        // localStorage.removeItem("clientRefinanceData"); // Keeping this in as it's part of the original logic
      } else {
        console.error("❌ Mercury API failed:", data);
        setErrorMsg(`Mercury API failed: ${data.message || "Unknown error — please try again."}`);
      }
    } catch (err) {
      console.error("❌ Submit error:", err);
      setErrorMsg(
        "Something went wrong submitting your refinance form. Please try again or contact our team."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <WelcomeScreen formData={formData} />;
  }

  // ---------------------------------------------
  // LIABILITY SUMMARY (for display)
  // ---------------------------------------------
  const allLiabilities = [
    ...(formData.applicant1?.liabilities || []),
    ...(formData.applicant2?.liabilities || []),
  ];

  const liabilitySummary =
    allLiabilities.length > 0
      ? allLiabilities
          .map((l) => {
            const parts = [];
            if (l.limit) parts.push(`limit $${Number(l.limit).toLocaleString()}`);
            if (l.balance) parts.push(`owing $${Number(l.balance).toLocaleString()}`);
            if (l.repayment) parts.push(`repayment $${Number(l.repayment).toLocaleString()}/mo`);
            const details = parts.length ? parts.join(", ") : "no amount listed";
            return `💳 ${l.type} – (${details})`;
          })
          .join("\n")
      : "None listed";

  const totalRepayments = allLiabilities.reduce((sum, l) => {
    const repay = parseFloat(l.repayment?.toString().replace(/,/g, "")) || 0;
    return sum + repay;
  }, 0);
  
  const formattedAddress = normaliseAddress(formData.propertyAddress);
  const propertyValue = formData.purchasePrice || formData.estimatedPropertyValue || 0;
  const loanBalance = formData.currentLoanBalance || 0;

  return (
    <div className="max-w-xl mx-auto p-6 md:p-10 bg-white rounded-2xl shadow-xl transition-all duration-300">
      <h3 className="text-3xl font-bold text-gray-800 mb-2 border-b pb-2">Submit Application</h3>
      <p className="text-gray-600 mb-6">
        Please review your details before confirming submission to Mortgage Matrix.
      </p>
      
      {errorMsg && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
          <p className="font-bold">Submission Error</p>
          <p className="text-sm">{errorMsg}</p>
        </div>
      )}

      <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 space-y-4">
        <h4 className="text-xl font-semibold text-indigo-700 border-b pb-2">Quick Summary</h4>

        {/* Property Section */}
        <div className="space-y-1">
          <h5 className="font-bold text-lg text-gray-800">Property Details</h5>
          <p className="text-sm">
            <span className="font-medium text-gray-600">Address:</span>{" "}
            <span className="break-words">{formattedAddress}</span>
          </p>
          <p className="text-sm">
            <span className="font-medium text-gray-600">Property Value:</span> ${Number(propertyValue).toLocaleString()}
          </p>
          <p className="text-sm">
            <span className="font-medium text-gray-600">Loan Balance:</span> ${Number(loanBalance).toLocaleString()}
          </p>
          <p className="text-sm">
            <span className="font-medium text-gray-600">Refinance Goal:</span> {formData.refinanceGoal || "N/A"}
          </p>
        </div>

        {/* Applicant 1 */}
        <div className="pt-3 border-t">
          <h5 className="font-bold text-lg text-gray-800">Applicant 1 Details</h5>
          <p className="text-sm">
            <span className="font-medium text-gray-600">Name:</span>{" "}
            {`${formData.applicant1?.firstName || ""} ${formData.applicant1?.lastName || ""}`}
          </p>
          <p className="text-sm">
            <span className="font-medium text-gray-600">Income:</span>{" "}
            {formData.applicant1?.income ? `$${Number(formData.applicant1.income).toLocaleString()}/yr` : "N/A"}
          </p>
          <p className="text-sm">
            <span className="font-medium text-gray-600">Employer:</span>{" "}
            {formData.applicant1?.employerName || "N/A"}
          </p>
        </div>

        {/* Co-Applicant */}
        {formData.refinancingWithSomeone && (
          <div className="pt-3 border-t">
            <h5 className="font-bold text-lg text-gray-800">Co-Applicant Details</h5>
            <p className="text-sm">
              <span className="font-medium text-gray-600">Name:</span>{" "}
              {`${formData.applicant2?.firstName || ""} ${formData.applicant2?.lastName || ""}`}
            </p>
            <p className="text-sm">
              <span className="font-medium text-gray-600">Income:</span>{" "}
              {formData.applicant2?.income ? `$${Number(formData.applicant2.income).toLocaleString()}/yr` : "N/A"}
            </p>
            <p className="text-sm">
              <span className="font-medium text-gray-600">Employer:</span>{" "}
              {formData.applicant2?.employerName || "N/A"}
            </p>
          </div>
        )}

        {/* Liabilities */}
        <div className="pt-3 border-t">
          <h5 className="font-bold text-lg text-gray-800">Liabilities</h5>
          <pre className="mt-2 p-3 bg-gray-100 rounded-lg text-xs text-gray-700 whitespace-pre-wrap font-mono">
            {liabilitySummary}
          </pre>
          {totalRepayments > 0 && (
            <p className="mt-2 text-sm font-semibold text-green-700">
              Total Monthly Repayments: ${Number(totalRepayments).toLocaleString()}
            </p>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <div className="mt-6">
        <button
          className={`w-full py-3 px-4 rounded-xl font-semibold text-white transition duration-200 ease-in-out shadow-lg 
            ${submitting ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}
          `}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <div className="flex items-center justify-center space-x-2">
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Submitting...</span>
            </div>
          ) : (
            "Submit Application"
          )}
        </button>
      </div>
    </div>
  );
}

export default SummarySubmit;